import type { BoundingBox, ThumbnailOptions } from '../types';

/**
 * Creates a square thumbnail from an image URL using a bounding box
 * @param imageUrl - The URL of the source image
 * @param boundingBox - The bounding box coordinates (0-1 fractions of image dimensions)
 * @param options - Optional configuration for size, padding, and quality
 * @returns Promise that resolves to an object URL of the cropped thumbnail
 */
export const createThumbnailFromUrl = async (
  imageUrl: string,
  boundingBox: BoundingBox,
  options: ThumbnailOptions = {}
): Promise<string> => {
  const {
    size = 48,
    padding = 10,
    quality = 0.9
  } = options;

  return new Promise((resolve, reject) => {
    // Create a new image element
    const img = new Image();

    // Set crossOrigin to handle CORS
    img.crossOrigin = 'anonymous';

    img.onload = (): void => {
      try {
        // Create canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // Set canvas size
        canvas.width = size;
        canvas.height = size;

        // Convert relative coordinates to absolute pixels
        const sourceX = boundingBox.left * img.naturalWidth;
        const sourceY = boundingBox.top * img.naturalHeight;
        const sourceWidth = boundingBox.width * img.naturalWidth;
        const sourceHeight = boundingBox.height * img.naturalHeight;

        // Calculate padding (10% of face size for better framing)
        const paddingPercent = 0.1;
        const paddingX = sourceWidth * paddingPercent;
        const paddingY = sourceHeight * paddingPercent;

        // Add padding with bounds checking
        const paddedX = Math.max(0, sourceX - paddingX - padding);
        const paddedY = Math.max(0, sourceY - paddingY - padding);
        const paddedWidth = Math.min(
          sourceWidth + (paddingX + padding) * 2,
          img.naturalWidth - paddedX
        );
        const paddedHeight = Math.min(
          sourceHeight + (paddingY + padding) * 2,
          img.naturalHeight - paddedY
        );

        // Draw the cropped face to canvas
        ctx.drawImage(
          img,
          paddedX,
          paddedY,
          paddedWidth,
          paddedHeight,
          0,
          0,
          size,
          size
        );

        // Convert to Blob (better performance than toDataURL)
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create blob from canvas'));
              return;
            }
            // Create object URL from blob
            const objectUrl = URL.createObjectURL(blob);
            resolve(objectUrl);
          },
          'image/jpeg',
          quality
        );
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = (): void => {
      reject(new Error(`Failed to load image: ${imageUrl}`));
    };

    // Load the image
    img.src = imageUrl;
  });
};

/**
 * Creates a cache key for thumbnail generation
 */
const getThumbnailCacheKey = (
  imageUrl: string,
  boundingBox: BoundingBox,
  size: number
): string => {
  return `${imageUrl}-${boundingBox.left}-${boundingBox.top}-${boundingBox.width}-${boundingBox.height}-${size}`;
};

// In-memory cache for generated thumbnails
const thumbnailCache = new Map<string, string>();

/**
 * Creates a thumbnail with caching to avoid regenerating the same thumbnail
 * @param imageUrl - The URL of the source image
 * @param boundingBox - The bounding box coordinates (0-1 fractions of image dimensions)
 * @param options - Optional configuration for size, padding, and quality
 * @returns Promise that resolves to an object URL of the cropped thumbnail
 */
export const createThumbnailFromUrlCached = async (
  imageUrl: string,
  boundingBox: BoundingBox,
  options: ThumbnailOptions = {}
): Promise<string> => {
  const { size = 48 } = options;
  const cacheKey = getThumbnailCacheKey(imageUrl, boundingBox, size);

  // Check cache first
  const cached = thumbnailCache.get(cacheKey);
  if (cached) {
    return Promise.resolve(cached);
  }

  // Generate thumbnail
  const thumbnail = await createThumbnailFromUrl(imageUrl, boundingBox, options);

  // Store in cache
  thumbnailCache.set(cacheKey, thumbnail);

  return thumbnail;
};

/**
 * Clears the thumbnail cache and revokes all object URLs to free memory
 */
export const clearThumbnailCache = (): void => {
  // Revoke all object URLs to free memory
  thumbnailCache.forEach((url) => {
    URL.revokeObjectURL(url);
  });
  thumbnailCache.clear();
};

/**
 * Revokes a specific thumbnail object URL to free memory
 * @param thumbnailUrl - The object URL to revoke
 */
export const revokeThumbnailUrl = (thumbnailUrl: string): void => {
  URL.revokeObjectURL(thumbnailUrl);
};
